import {ActionMenuLayout} from '#/components/actionmenu/action-menu-layout';
import {ActionMenuLandInformationNotFound} from '#/components/actionmenu/sections/action-menu-land-information-not-found';
import {ActionMenuLandLoading} from '#/components/actionmenu/sections/action-menu-land-loading';
import {ActionMenuLandNotFound} from '#/components/actionmenu/sections/action-menu-land-not-found';
import {ActionMenuUnlistedLand} from '#/components/actionmenu/sections/action-menu-unlisted-land';
import {ActionMenuInformation} from '#/components/actionmenu/sections/information/action-menu-information';
import {ActionMenuRent} from '#/components/actionmenu/sections/rent/action-menu-rent';
import {ActionMenuTrade} from '#/components/actionmenu/sections/trade/action-menu-trade';
import {ActionMenuTransaction} from '#/components/actionmenu/sections/transaction/action-menu-transaction';
import {TransactionData} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {ActionMenuSection} from '#/components/actionmenu/types';
import {SLQueryParamConfig} from '#/contexts/query-params-provider';
import {UIStatusContext} from '#/contexts/ui-status-provider';
import {WalletContext} from '#/contexts/wallet-provider';
import {ShareLandMarketContract} from '#/contracts/ShareLandMarketContract';
import {useLandAccountInfo} from '#/hooks/query/use-land-account-info';
import {useLandMarketInfo} from '#/hooks/query/use-land-market-info';
import {useLandTokenContract} from '#/hooks/query/use-land-token-contract';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {useSelectedLand} from '#/hooks/use-selected-land';
import {landHasMarket} from '#/utils/land-utils';
import {FunctionComponent, createContext, useContext, useEffect, useState} from 'react';
import {Land, LandAccountInfo, LandMarketInfo} from 'shareland-common';
import {useQueryParams} from 'use-query-params';

type TActionMenuContext = {
  land: Land;
  landMarketInfo: LandMarketInfo;
  landAccountInfo: LandAccountInfo;
  landTokenContract: ShareLandMarketContract | null;
  transaction?: TransactionData;
  initiateTransaction: (data: TransactionData) => void;
};
export const ActionMenuContext = createContext<TActionMenuContext>({} as TActionMenuContext);

export const ActionMenu: FunctionComponent = () => (
  <ActionMenuLayout>
    <ActionMenuContent />
  </ActionMenuLayout>
);

export const ActionMenuContent: FunctionComponent = () => {
  const {isActionMenuOpen: showActionMenu} = useContext(UIStatusContext);

  const {data: selectedLand, isLoading: selectedLandIsLoading} = useSelectedLand();

  if (!showActionMenu) {
    return null;
  }

  if (selectedLandIsLoading || !showActionMenu) {
    return <ActionMenuLandLoading land={undefined} />;
  }

  if (!selectedLand) {
    return <ActionMenuLandNotFound />;
  }

  if (!landHasMarket(selectedLand)) {
    return <ActionMenuUnlistedLand unlistedLand={selectedLand} />;
  }

  return <ActionMenuLand land={selectedLand} />;
};

const ActionMenuLand: FunctionComponent<{
  land: Land;
}> = ({land}) => {
  const {
    accountResult: {address},
  } = useContext(WalletContext);
  const {
    data: landMarketInfo,
    isLoading: landMarketInfoIsLoading,
    refetch: landMarketInfoRefetch,
  } = useLandMarketInfo(land);
  const {data: landAccountInfo, isLoading: landAccountInfoIsLoading} = useLandAccountInfo(land, address);

  useEffect(() => {
    landMarketInfoRefetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [land.id]);

  const landTokenContract = useLandTokenContract(land);

  const [{action}] = useQueryParams<SLQueryParamConfig>();

  const [transaction, setTransaction] = useState<TransactionData>();

  const {
    actionMenu: {openTransaction},
  } = useQueryNavigationActions();

  const initiateTransaction = (data: TransactionData) => {
    setTransaction(data);
    openTransaction();
  };

  if (landMarketInfoIsLoading || landAccountInfoIsLoading) {
    return <ActionMenuLandLoading land={land} />;
  }

  if (!landMarketInfo || !landAccountInfo) {
    return <ActionMenuLandInformationNotFound land={land} />;
  }

  return (
    <ActionMenuContext.Provider
      value={{
        land,
        landMarketInfo,
        landAccountInfo,
        landTokenContract,
        transaction,
        initiateTransaction,
      }}
    >
      {action === ActionMenuSection.INFORMATION && <ActionMenuInformation />}
      {action === ActionMenuSection.TRADE && <ActionMenuTrade />}
      {action === ActionMenuSection.RENT && <ActionMenuRent />}
      {action === ActionMenuSection.TRANSACTION && <ActionMenuTransaction />}
    </ActionMenuContext.Provider>
  );
};
